const MIB = 1024 * 1024

/** Base multipart part size (16 MB, binary). */
export const BASE_PART_SIZE_BYTES = 16 * MIB
/** S3 and Spaces allow at most 10,000 parts per multipart upload. */
export const MAX_PART_COUNT = 10_000

export interface PartLayout {
  partSizeBytes: number
  partCount: number
}

/**
 * Part size grows in whole MiB steps so there are never more than 10,000 parts.
 * The server decides the real layout and returns it with the upload ticket; this
 * function mirrors that rule so the client can validate the ticket.
 */
export function computePartLayout(totalBytes: number): PartLayout {
  const needed = Math.ceil(totalBytes / MAX_PART_COUNT)
  const partSizeBytes = Math.max(BASE_PART_SIZE_BYTES, Math.ceil(needed / MIB) * MIB)
  return { partSizeBytes, partCount: Math.max(1, Math.ceil(totalBytes / partSizeBytes)) }
}

/** Byte range [start, end) of part `partNumber` (1-based). The last part is shorter. */
export function partRange(
  partNumber: number,
  partSizeBytes: number,
  totalBytes: number,
): { start: number; end: number } {
  const start = (partNumber - 1) * partSizeBytes
  return { start, end: Math.min(totalBytes, partNumber * partSizeBytes) }
}

/** True when the ticket layout is consistent with the blob size. */
export function isLayoutConsistent(layout: PartLayout, totalBytes: number): boolean {
  return (
    layout.partSizeBytes > 0 &&
    layout.partCount >= 1 &&
    layout.partCount <= MAX_PART_COUNT &&
    layout.partCount === Math.ceil(totalBytes / layout.partSizeBytes)
  )
}
